import { useEffect, type ReactNode } from "react";

export interface ModalProps {
  title?: ReactNode;
  children: ReactNode;
  /** Rendered in the pinned footer (usually action buttons). */
  footer?: ReactNode;
  onClose: () => void;
  /** Max width utility (Tailwind agd: class). Defaults to a small dialog. */
  widthClassName?: string;
}

/**
 * Overlay dialog with a pinned header + footer; only the middle region scrolls
 * when content is tall. Closes on backdrop click and Escape.
 */
export default function Modal({ title, children, footer, onClose, widthClassName = "agd:max-w-[420px]" }: ModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="agd:fixed agd:inset-0 agd:z-[9998] agd:flex agd:items-center agd:justify-center agd:bg-black/45 agd:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`agd:flex agd:max-h-[85vh] agd:w-full agd:flex-col agd:overflow-hidden agd:rounded-xl agd:border-t-4 agd:border-accent agd:bg-card agd:shadow-2xl ${widthClassName}`}
      >
        {title ? (
          <h3 className="agd:m-0 agd:px-8 agd:pt-7 agd:pb-3 agd:text-lg agd:font-bold agd:text-foreground">{title}</h3>
        ) : null}
        <div className="agd:min-h-0 agd:flex-1 agd:overflow-auto agd:px-8 agd:py-2">{children}</div>
        {footer ? (
          <div className="agd:flex agd:justify-end agd:gap-2.5 agd:border-t agd:border-border agd:px-8 agd:py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
